import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseQueryOptions,
} from '@tanstack/react-query';
import { WorkspaceCoreValue } from '@/common/types/databaseTypes';
import { workspaceCoreValueService } from '@tickitacka/shared';

// 핵심 가치 쿼리 키 상수
const COREVALUE_KEYS = {
    all: ['workspaceCoreValues'] as const,
    list: (workspaceId: string | undefined) =>
        [...COREVALUE_KEYS.all, workspaceId] as const,
};

export const useWorkspaceCoreValues = (
    workspaceId: string | undefined,
    options?: Omit<
        UseQueryOptions<
            WorkspaceCoreValue[],
            Error,
            WorkspaceCoreValue[],
            ReturnType<typeof COREVALUE_KEYS.list>
        >,
        'queryKey' | 'queryFn'
    >
) => {
    return useQuery<
        WorkspaceCoreValue[],
        Error,
        WorkspaceCoreValue[],
        ReturnType<typeof COREVALUE_KEYS.list>
    >({
        queryKey: COREVALUE_KEYS.list(workspaceId),
        queryFn: () => {
            if (!workspaceId) {
                return Promise.resolve([]);
            }
            return workspaceCoreValueService.getWorkspaceCoreValues(workspaceId);
        },
        enabled: !!workspaceId,
        ...options,
    });
};

export const useAddWorkspaceCoreValue = () => {
    const queryClient = useQueryClient();

    return useMutation<
        WorkspaceCoreValue,
        Error,
        { workspaceId: string; name: string }
    >({
        mutationFn: ({ workspaceId, name }) => {
            return workspaceCoreValueService.addWorkspaceCoreValue(
                workspaceId,
                name
            );
        },
        onSuccess: (_, { workspaceId }) => {
            void queryClient.invalidateQueries({
                queryKey: COREVALUE_KEYS.list(workspaceId),
            });
        },
    });
};

export const useDeleteWorkspaceCoreValue = () => {
    const queryClient = useQueryClient();

    return useMutation<
        boolean,
        Error,
        { coreValueId: string; workspaceId: string }
    >({
        mutationFn: ({ coreValueId }) => {
            return workspaceCoreValueService.deleteWorkspaceCoreValue(
                coreValueId
            );
        },
        onSuccess: (_, { workspaceId }) => {
            void queryClient.invalidateQueries({
                queryKey: COREVALUE_KEYS.list(workspaceId),
            });
        },
    });
};
